import React from "react";
import { EmptyState } from "@/modules/common/components/ui-kit/empty-state";
import ReceiptTextIcon from "@/assets/icons/lead-page/recepit-text";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import useGetLeadSales from "@/modules/lead-page/hooks/get-lead-sales";
import { SalesCard } from "@/modules/lead-page/new/components/sales/sales-card";
import { SalesCardSkeleton } from "@/modules/lead-page/new/components/sales/sales-card-skeleton";
import { LeadSale } from "@/modules/lead-page/types";
import { Box, useTheme } from "@mui/material";
import ArchivedOverlay from "@/modules/common/components/ui-kit/archived-overlay";
import { useContext } from "react";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";

const SalesContent: React.FC = () => {
  const { leadSales, isLoading, isError, noSales } = useGetLeadSales();
  const theme = useTheme();
  const { lead } = useContext(LeadPageContext);
  const isLeadArchived = lead?.data?.lead?.archived;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.md,
          height: "100%",
          overflowY: "auto",
        }}
      >
        <SalesCardSkeleton count={2} />
      </Box>
    );
  }

  if (noSales) {
    return (
      <EmptyState
        icon={<ReceiptTextIcon />}
        label="No sales yet"
        description="This lead has no sales yet."
      />
    );
  }

  if (isError) {
    return (
      <EmptyState
        icon={<ReceiptTextIcon />}
        label="Error loading sales"
        description="There was an error fetching sales data."
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.md,
        padding: theme.nawy.spacing.md,
        height: "100%",
        overflowY: "auto",
        backgroundColor: theme.nawy.colors.semantic.layer.primary,
        borderRadius: theme.nawy.radius["2xl"],
      }}
    >
      {leadSales?.map((sale: LeadSale, index: number) => (
        <ArchivedOverlay isArchived={isLeadArchived}>
          <SalesCard key={`${sale.unit_number} + ${index}`} data={sale} />
        </ArchivedOverlay>
      ))}
    </Box>
  );
};

export const SalesTab: React.FC = () => {
  return (
    <PermissionGuard
      permissions={[
        {
          name: PermissionsEnum.JOURNEY_SALES,
          level: PermissionLevel.READ,
        },
      ]}
    >
      <SalesContent />
    </PermissionGuard>
  );
};

export default SalesTab;
